import { globals } from 'config/globals';
import { Rect, Range } from 'app/contracts/Geometry';
export enum TimelineRegionType {
	RECTANGLE = 'rectangle',
	PROGRESS = 'progress',
	CONTAINER = 'container'
}

/**
 * Region config + runtime wiring. Only id/type (+ rect or range, by type)
 * come from the data (HomeConfig.regions) — the constructor null-guards both.
 * onEnter/onExit/pixiContainer are attached at runtime by
 * HomeTimeline._createTimelineRegion before the manager.add call.
 * `rect.x2`/`rect.w2` appear in the legacy data but are never read.
 */
export interface TimelineRegionParams {
	id: string;
	type: TimelineRegionType;
	rect?: Rect & { x2?: number; w2?: number };
	range?: Range;
	onEnter?: Function;
	onExit?: Function;
	pixiContainer?: PIXI.Container;
}

interface MouseCords {
	x: number;
	y: number;
	relX: number;
	relY: number;
}

export class TimelineRegion {
	private _id: string;
	private _type: TimelineRegionType;
	private _rect: Rect;
	private _range: Range;
	private _onEnter: Function;
	private _onExit: Function;
	private _intersecting: boolean = false;
	private _entered: boolean = false;

	public get id(): string {
		return this._id;
	}

	public constructor(params: TimelineRegionParams) {
		this._id = params.id;
		this._type = params.type;
		this._rect = params.rect || null;
		this._range = params.range || null;
		this._onEnter = params.onEnter;
		this._onExit = params.onExit;
	}

	public tick(mouseCords: MouseCords, progress: number, velocity: number, containerX: number = null) {
		if (this._type === TimelineRegionType.RECTANGLE) {
			this._processRectangle(mouseCords, velocity);
		} else if (this._type === TimelineRegionType.PROGRESS) {
			this._processProgress(progress);
		} else if (this._type === TimelineRegionType.CONTAINER) {
			this._processContainer(containerX);
		}
	}

	private _processRectangle(mouseCords: MouseCords, velocity: number) {
		this._intersecting =
			mouseCords.relX > this._rect.x &&
			mouseCords.relX < this._rect.x + this._rect.w &&
			mouseCords.relY > this._rect.y &&
			mouseCords.relY < this._rect.y + this._rect.h;

		this._checkEnterState(velocity, true);
	}

	private _processProgress(progress: number): void {
		this._intersecting = progress > this._range.min && progress <= this._range.max;
		this._checkEnterState();
	}

	private _processContainer(progress: number): void {}

	private _checkEnterState(velocity: number = 0, checkMouse: boolean = false): void {
		let mouseDown: boolean = checkMouse && globals.touchActive;

		if (!this._entered && this._intersecting && Math.abs(velocity) < 0.02 && !mouseDown) {
			this._entered = true;
			this._onEnter(this._id);
		} else if (this._entered && !this._intersecting) {
			this._entered = false;
			this._onExit(this._id);
		}
	}
}
